import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch,
  faEllipsisV,
  faPaperPlane,
  faSmile,
  faUser,
  faCrown,
  faArrowLeft,
  faCheckDouble,
  faCircle,
  faFilter,
  faCalendarAlt,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import EmojiPicker from 'emoji-picker-react';
import TourContext from '../../context/TourContext';
import UserContext from '../../context/UserContext';
import ChatContext from '../../context/ChatContext';
import LeftMenu from '../LeftMenu';
import styles from './Messages.module.css';
import { 
  doc, 
  getDoc, 
  setDoc, 
  addDoc, 
  collection, 
  serverTimestamp 
} from 'firebase/firestore';
import { getFirestoreInstance } from '../../utils/firebaseHelper';

const MessagesLayout = () => {
  const [messageInput, setMessageInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [userProfiles, setUserProfiles] = useState({});
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);
  const [directContact, setDirectContact] = useState(null);
  const [showAllContacts, setShowAllContacts] = useState(false);
  
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const messagesAreaRef = useRef(null);
  
  const navigate = useNavigate();
  const { selectedTour, tours } = useContext(TourContext);
  const { userData, getProfileData } = useContext(UserContext);
  const { 
    messages, 
    chatList,
    activeChat, 
    loading, 
    loadingMore,
    loadingChatList,
    sendMessage, 
    selectChat, 
    directSelectChat,
    setActiveChat,
    loadMoreMessages,
    hasMoreMessages
  } = useContext(ChatContext);

  // Helper function to check if a tour has dates in the current month
  const isTourInCurrentMonth = useCallback((tour) => {
    if (!tour || !tour.tourDates || tour.tourDates.length === 0) {
      return false;
    }

    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    return tour.tourDates.some(date => {
      // Check both original date and rescheduled date if it exists
      const originalDate = new Date(date.date);
      
      if (originalDate.getMonth() === currentMonth && 
          originalDate.getFullYear() === currentYear && 
          !date.cancelled) {
        return true;
      }
      
      // Check rescheduled date if it exists
      if (date.rescheduleDate?.isRescheduled) {
        const rescheduledDate = new Date(date.rescheduleDate.newDate);
        return rescheduledDate.getMonth() === currentMonth && 
               rescheduledDate.getFullYear() === currentYear && 
               !date.cancelled;
      }
      
      return false;
    });
  }, []);

  // Check for direct contact in sessionStorage
  useEffect(() => {
    try {
      const directContactJSON = sessionStorage.getItem('directContactUser');
      if (directContactJSON) {
        console.log('[MESSAGES] Found direct contact in session storage');
        
        // Parse the contact data
        const contactData = JSON.parse(directContactJSON);
        
        // Remove from sessionStorage to prevent reuse on page refresh
        sessionStorage.removeItem('directContactUser');
        
        if (contactData && contactData.userId && userData?.userId) {
          // Set the direct contact
          setDirectContact(contactData);
          
          // Create a chat ID based on user IDs
          const chatId = [userData.userId, contactData.userId].sort().join('_');
          console.log('[MESSAGES] Setting temporary chat ID:', chatId);
          
          // Set active chat - this just sets the UI state, doesn't create a Firestore document yet
          setActiveChat(chatId);
        }
      }
    } catch (error) {
      console.error('[MESSAGES] Error processing direct contact:', error);
    }
  }, [userData?.userId, setActiveChat]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current && activeChat) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, activeChat]);

  // Scroll event listener for loading more messages
  useEffect(() => {
    const handleScroll = () => {
      if (!messagesAreaRef.current || !activeChat || loadingMore) return;
      
      const { scrollTop } = messagesAreaRef.current;
      
      // If user has scrolled to the top (or near the top), load more messages
      if (scrollTop < 50 && hasMoreMessages[activeChat]) {
        loadMoreMessages();
      }
    };
    
    const messagesArea = messagesAreaRef.current;
    if (messagesArea) {
      messagesArea.addEventListener('scroll', handleScroll);
    }
    
    return () => {
      if (messagesArea) {
        messagesArea.removeEventListener('scroll', handleScroll);
      }
    };
  }, [activeChat, loadMoreMessages, loadingMore, hasMoreMessages]);

  // Emoji picker outside click handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get profile data for all chat contacts
  useEffect(() => {
    const fetchUserProfiles = async () => {
      if (!chatList.length || !userData?.userId) return;
      
      setIsLoadingProfiles(true);
      
      const uniqueUserIds = [...new Set(chatList.map(chat => chat.otherUserId))];
      const userProfilesMap = { ...userProfiles };
      const fetchPromises = [];
      
      // Only fetch profiles we don't already have
      for (const userId of uniqueUserIds) {
        if (!userProfilesMap[userId]) {
          fetchPromises.push(
            getProfileData(userId)
              .then(profile => {
                if (profile) {
                  userProfilesMap[userId] = {
                    userId: profile.userId || userId,
                    fullName: profile.fullName || 'Unknown User',
                    profilePictureUrl: profile.profilePictureUrl || '',
                    userType: profile.userType || 'User',
                    userName: profile.userName || '',
                    // Check if this user is in a tour
                    tourName: findUserTour(userId)
                  };
                }
              })
              .catch(error => {
                console.error(`[UI] Error fetching profile for ${userId}:`, error);
                // Add placeholder for failed fetches to avoid repeated attempts
                userProfilesMap[userId] = {
                  userId,
                  fullName: 'Unknown User',
                  userType: 'User'
                };
              })
          );
        }
      }
      
      if (fetchPromises.length > 0) {
        await Promise.all(fetchPromises);
        setUserProfiles(userProfilesMap);
      }
      
      setIsLoadingProfiles(false);
    };
    
    fetchUserProfiles();
  }, [chatList, userData?.userId, getProfileData, tours]);

  // Find tour for a user if they are part of a tour
  const findUserTour = useCallback((userId) => {
    if (!tours || !tours.length) return '';
    
    for (const tour of tours) {
      // Check artist members
      const isArtist = tour.artistDetails?.some(artist => artist.userId === userId);
      if (isArtist) return tour.tourName || 'Unnamed Tour';
      
      // Check crew members
      const isCrew = tour.crewDetails?.some(crew => crew.userId === userId);
      if (isCrew) return tour.tourName || 'Unnamed Tour';
      
      // Check manager
      if (tour.createdBy?.userId === userId) {
        return tour.tourName || 'Unnamed Tour';
      }

      // Check vendor members
      const isVendor = tour.vendorDetails?.some(vendor => vendor.userId === userId);
      if (isVendor) return tour.tourName || 'Unnamed Tour';

      // Check staff members
      const isStaff = tour.staffDetails?.some(staff => staff.userId === userId);
      if (isStaff) return tour.tourName || 'Unnamed Tour';
    }
    
    return '';
  }, [tours]);

  // Check if a user is part of the selected tour
  const isUserInSelectedTour = useCallback((userId) => {
    if (!selectedTour || !userId) return false;
    
    // Check all possible roles
    return (
      selectedTour.artistDetails?.some(artist => artist.userId === userId) ||
      selectedTour.crewDetails?.some(crew => crew.userId === userId) ||
      selectedTour.managerDetails?.some(manager => manager.userId === userId) ||
      selectedTour.vendorDetails?.some(vendor => vendor.userId === userId) ||
      selectedTour.staffDetails?.some(staff => staff.userId === userId) ||
      selectedTour.createdBy?.userId === userId
    );
  }, [selectedTour]);

  // Get all tour members, filtered by current month if applicable
  const allTourMembers = React.useMemo(() => {
    if (!tours || !tours.length) return [];
    
    const uniqueUsers = new Map();
    
    // First check if selected tour has dates in the current month
    const currentTour = selectedTour && isTourInCurrentMonth(selectedTour) ? selectedTour : null;
    
    // If we have a valid current tour, only show members from that tour
    if (currentTour && !showAllContacts) {
      // Add artists
      currentTour.artistDetails?.forEach(artist => {
        if (!uniqueUsers.has(artist.userId)) {
          uniqueUsers.set(artist.userId, {
            ...artist,
            tourName: currentTour.tourName || 'Unnamed Tour'
          });
        }
      });
      
      // Add crew members
      currentTour.crewDetails?.forEach(crew => {
        if (!uniqueUsers.has(crew.userId)) {
          uniqueUsers.set(crew.userId, {
            ...crew,
            tourName: currentTour.tourName || 'Unnamed Tour'
          });
        }
      });
      
      // Add tour manager
      if (currentTour.createdBy && !uniqueUsers.has(currentTour.createdBy.userId)) {
        uniqueUsers.set(currentTour.createdBy.userId, {
          ...currentTour.createdBy,
          userType: 'Manager',
          tourName: currentTour.tourName || 'Unnamed Tour'
        });
      }

      // Add vendor members
      currentTour.vendorDetails?.forEach(vendor => {
        if (!uniqueUsers.has(vendor.userId)) {
          uniqueUsers.set(vendor.userId, {
            ...vendor,
            tourName: currentTour.tourName || 'Unnamed Tour'
          });
        }
      });

      // Add staff members
      currentTour.staffDetails?.forEach(staff => {
        if (!uniqueUsers.has(staff.userId)) {
          uniqueUsers.set(staff.userId, {
            ...staff,
            tourName: currentTour.tourName || 'Unnamed Tour'
          });
        }
      });
    }
    // Otherwise show members from all tours (if showAllContacts is true)
    else if (showAllContacts) {
      tours.forEach(tour => {
        // Add artists
        tour.artistDetails?.forEach(artist => {
          if (!uniqueUsers.has(artist.userId)) {
            uniqueUsers.set(artist.userId, {
              ...artist,
              tourName: tour.tourName || 'Unnamed Tour'
            });
          }
        });
        
        // Add crew members
        tour.crewDetails?.forEach(crew => {
          if (!uniqueUsers.has(crew.userId)) {
            uniqueUsers.set(crew.userId, {
              ...crew,
              tourName: tour.tourName || 'Unnamed Tour'
            });
          }
        });
        
        // Add tour manager
        if (tour.createdBy && !uniqueUsers.has(tour.createdBy.userId)) {
          uniqueUsers.set(tour.createdBy.userId, {
            ...tour.createdBy,
            userType: 'Manager',
            tourName: tour.tourName || 'Unnamed Tour'
          });
        }

        // Add vendor members
        tour.vendorDetails?.forEach(vendor => {
          if (!uniqueUsers.has(vendor.userId)) {
            uniqueUsers.set(vendor.userId, {
              ...vendor,
              tourName: tour.tourName || 'Unnamed Tour'
            });
          }
        });

        // Add staff members
        tour.staffDetails?.forEach(staff => {
          if (!uniqueUsers.has(staff.userId)) {
            uniqueUsers.set(staff.userId, {
              ...staff,
              tourName: tour.tourName || 'Unnamed Tour'
            });
          }
        });
      });
    }
    
    if (userData?.userId) {
      uniqueUsers.delete(userData.userId);
    }
    
    return Array.from(uniqueUsers.values());
  }, [tours, userData?.userId, selectedTour, isTourInCurrentMonth, showAllContacts]);

  // Create combined contacts list from chat list and user profiles
  const combinedContacts = React.useMemo(() => {
    if (!userData?.userId) return [];
    
    // Create a map to store merged contact data
    const contactMap = new Map();
    
    // Add contacts from chat list first (all existing chats always show)
    chatList.forEach(chat => {
      const profile = userProfiles[chat.otherUserId];
      if (profile) {
        contactMap.set(chat.otherUserId, {
          ...profile,
          chatId: chat.chatId,
          lastMessage: chat.lastMessage || '',
          timestamp: chat.timestamp || 0,
          unread: chat.unread || false,
          hasExistingChat: true
        });
      }
    });
    
    // Add tour members who aren't in chats (filtered by tour selection if applicable)
    allTourMembers.forEach(member => {
      if (!contactMap.has(member.userId)) {
        contactMap.set(member.userId, {
          ...member,
          lastMessage: '',
          timestamp: 0,
          unread: false,
          hasExistingChat: false
        });
      } else {
        // If user exists in both, ensure tour info is preserved
        const existing = contactMap.get(member.userId);
        contactMap.set(member.userId, {
          ...existing,
          tourName: member.tourName || existing.tourName,
          hasExistingChat: true
        });
      }
    });
    
    // Convert to array and sort
    // Priority: 1. Unread messages, 2. Existing chats, 3. Timestamp
    const contacts = Array.from(contactMap.values()).sort((a, b) => {
      if (a.unread && !b.unread) return -1;
      if (!a.unread && b.unread) return 1;
      if (a.hasExistingChat && !b.hasExistingChat) return -1;
      if (!a.hasExistingChat && b.hasExistingChat) return 1;
      return b.timestamp - a.timestamp;
    });
    
    return contacts;
  }, [chatList, allTourMembers, userProfiles, userData?.userId]);

  // Filter contacts based on search
  const filteredContacts = React.useMemo(() => {
    return combinedContacts.filter(contact => 
      !searchQuery.trim() || 
      contact.fullName?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [combinedContacts, searchQuery]);

  // Define a function to determine if the contacts should include the direct contact
  const getDisplayedContacts = useCallback(() => {
    // Start with the filtered contacts from the chatList
    let contacts = [...filteredContacts];
    
    // If we have a direct contact that isn't already in the list, add it
    if (directContact && !contacts.some(contact => contact.userId === directContact.userId)) {
      contacts = [directContact, ...contacts];
    }
    
    return contacts;
  }, [filteredContacts, directContact]);

  // Get current chat user
  const currentChatUser = React.useMemo(() => {
    if (!activeChat || !userData?.userId) return null;
    
    // If we have a direct contact and this is its chat
    if (directContact && activeChat.includes(directContact.userId)) {
      return directContact;
    }
    
    // Extract the other user ID from the active chat ID
    const chatParticipants = activeChat.split('_');
    const otherUserId = chatParticipants.find(id => id !== userData.userId);
    
    if (!otherUserId) return null;
    
    // First check combined contacts
    const fromContacts = combinedContacts.find(contact => contact.userId === otherUserId);
    if (fromContacts) return fromContacts;
    
    // If not found, check userProfiles
    return userProfiles[otherUserId] || null;
  }, [activeChat, combinedContacts, userProfiles, userData?.userId, directContact]);

  // Navigate to user profile
  const navigateToUserProfile = useCallback((username) => {
    if (username) {
      navigate(`/${username}`);
    }
  }, [navigate]);

  // Handle chat selection
  const handleChatSelect = useCallback((contact) => {
    if (!contact?.userId || !userData?.userId) return;
    
    console.log('[UI] Selecting chat with user:', contact.fullName);
    
    // If we already have a chatId, use it directly
    if (contact.chatId) {
      setActiveChat(contact.chatId);
    } else {
      // Otherwise create a new chat
      selectChat(contact);
    }
  }, [selectChat, setActiveChat, userData?.userId]);

  // Handle mobile back button
  const handleMobileBack = useCallback(() => {
    setActiveChat(null);
  }, [setActiveChat]);

  // Handle sending message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageInput.trim()) return;

    try {
      // Save the message input and clear it immediately for better UX
      const messageToSend = messageInput.trim();
      setMessageInput('');
      
      // If this is a direct contact that doesn't have a real chat yet
      if (directContact && activeChat && activeChat.includes(directContact.userId)) {
        // Check if the chat already exists in Firestore
        const db = getFirestoreInstance();
        const chatDoc = await getDoc(doc(db, 'Chats', activeChat));
        
        if (!chatDoc.exists()) {
          console.log('[MESSAGES] Creating new chat in Firestore:', activeChat);
          
          // Create the chat document
          await setDoc(doc(db, 'Chats', activeChat), {
            participants: activeChat.split('_'),
            created: serverTimestamp(),
            lastUpdated: serverTimestamp(),
            lastMessage: messageToSend,
            unreadCount: {
              [userData.userId]: 0,
              [directContact.userId]: 1
            }
          });
          
          // Create initial messages collection
          const messagesRef = collection(db, 'Chats', activeChat, 'messages');
          await addDoc(messagesRef, {
            content: messageToSend,
            senderId: userData.userId,
            timestamp: Date.now(),
            read: false
          });
          
          // Clear direct contact as it's now a real chat
          setDirectContact(null);
        } else {
          // Chat exists, just send the message normally
          await sendMessage(messageToSend);
        }
      } else {
        // Normal case - use the existing sendMessage function
        await sendMessage(messageToSend);
      }
      
      // Focus the input for next message
      messageInputRef.current?.focus();
      
      // Scroll to the bottom after sending a message
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error('[MESSAGES] Error sending message:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  // Handle input height adjustment
  const adjustInputHeight = (e) => {
    const element = e.target;
    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, 100)}px`;
  };

  // Emoji selection handler
  const handleEmojiSelect = (emojiObject) => {
    const emoji = emojiObject.emoji;
    setMessageInput(prev => prev + emoji);
    setShowEmojiPicker(false);
    messageInputRef.current?.focus();
  };

  // Render message status icon
  const renderMessageStatus = (message) => {
    if (message.senderId !== userData?.userId) return null;

    return (
      <div className={`${styles.messageStatus} ${message.read ? styles.read : styles.sent}`}>
        <FontAwesomeIcon icon={faCheckDouble} />
      </div>
    );
  };

  // Format timestamp for display
  const formatLastMessageTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      // Today - show time
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      // Yesterday
      return 'Yesterday';
    } else if (diffDays < 7) {
      // Last week - show day name
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      // Older - show date
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.contentLayout}>
        <LeftMenu />
        
        <div className={styles.messagesContainer}>
          {/* Contacts List */}
          <div className={`${styles.chatListPanel} ${activeChat && isMobile ? styles.hidden : ''}`}>
            <div className={styles.chatListHeader}>
              <h2>Messages</h2>
              <div className={styles.searchContainer}>
                <input 
                  type="text"
                  placeholder="Search contacts"
                  className={styles.searchInput}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Filter toggle */}
            <div className={styles.filterSection}>
              <button 
                className={`${styles.filterButton} ${!showAllContacts ? styles.active : ''}`}
                onClick={() => setShowAllContacts(false)}
                title="Show users from current month's tour"
              >
                <FontAwesomeIcon icon={faCalendarAlt} />
                <span>Current Tour</span>
              </button>
              <button 
                className={`${styles.filterButton} ${showAllContacts ? styles.active : ''}`}
                onClick={() => setShowAllContacts(true)}
                title="Show all contacts"
              >
                <FontAwesomeIcon icon={faFilter} />
                <span>All Contacts</span>
              </button>
            </div>

            <div className={styles.contactsSection}>
              {loadingChatList || isLoadingProfiles ? (
                <div className={styles.loading}>Loading contacts...</div>
              ) : getDisplayedContacts().length > 0 ? (
                getDisplayedContacts().map(contact => (
                  <div 
                    key={contact.userId}
                    className={`${styles.userCard} ${
                      activeChat?.includes(contact.userId) ? styles.active : ''
                    }`}
                    onClick={() => handleChatSelect(contact)}
                  >
                    <div className={styles.userAvatar}>
                      {contact.profilePictureUrl ? (
                        <img src={contact.profilePictureUrl} alt={contact.fullName} />
                      ) : (
                        <FontAwesomeIcon icon={faUser} />
                      )}
                    </div>
                    <div className={styles.userInfo}>
                      <div className={styles.userName}>
                        {contact.fullName}
                        {contact.userType === 'Manager' && (
                          <FontAwesomeIcon 
                            icon={faCrown} 
                            className={styles.managerIcon} 
                            style={{ marginLeft: '5px', color: '#FFD700' }}
                          />
                        )}
                        {contact.unread && (
                          <FontAwesomeIcon 
                            icon={faCircle} 
                            className={styles.unreadIcon} 
                            style={{ marginLeft: '5px', color: '#E2384D', fontSize: '10px' }}
                          />
                        )}
                      </div>
                      <div className={styles.lastMessage}>
                        {contact.lastMessage || (contact.tourName ? `From ${contact.tourName}` : 'New contact')}
                      </div>
                    </div>
                    {contact.timestamp > 0 && (
                      <div className={styles.messageTime} style={{ 
                        fontSize: '12px', 
                        color: 'var(--uitext-secondary)', 
                        marginLeft: '10px' 
                      }}>
                        {formatLastMessageTime(contact.timestamp)}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className={styles.emptyChatState}>
                  {!showAllContacts && selectedTour ? 
                    "No contacts available in current month's tour" :
                    "No contacts found"}
                </div>
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className={`${styles.chatArea} ${activeChat || !isMobile ? styles.active : ''}`}>
            {activeChat && currentChatUser ? (
              <>
                <div className={styles.chatHeader}>
                  {isMobile && (
                    <button 
                      className={styles.backButton}
                      onClick={handleMobileBack}
                    >
                      <FontAwesomeIcon icon={faArrowLeft} />
                    </button>
                  )}
                  <div 
                    className={styles.chatUserInfo}
                    onClick={() => navigateToUserProfile(currentChatUser.userName)}
                  >
                    <div className={styles.userAvatar}>
                      {currentChatUser.profilePictureUrl ? (
                        <img 
                          src={currentChatUser.profilePictureUrl} 
                          alt={currentChatUser.fullName}
                          className={styles.chatUserAvatar}
                        />
                      ) : (
                        <FontAwesomeIcon icon={faUser} />
                      )}
                    </div>
                    <div>
                      <div className={styles.chatUserName}>
                        {currentChatUser.fullName}
                      </div>
                      <div className={styles.lastMessage}>
                        {currentChatUser.tourName || 'Direct message'}
                      </div>
                    </div>
                  </div>
                </div>

                <div 
                  className={styles.messagesArea}
                  ref={messagesAreaRef}
                >
                  {loading && !messages[activeChat]?.length ? (
                    <div className={styles.loading}>Loading messages...</div>
                  ) : messages[activeChat]?.length > 0 ? (
                    <>
                      {/* Loading more messages indicator */}
                      {loadingMore && (
                        <div className={styles.loadingMore}>
                          <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '8px' }} />
                          Loading more messages...
                        </div>
                      )}
                      
                      {messages[activeChat].map((message) => (
                        <div 
                          key={message.id || message.timestamp}
                          className={`${styles.messageItem} ${
                            message.senderId === userData?.userId ? styles.sent : ''
                          }`}
                        >
                          <div className={styles.messageContent}>
                            <div className={styles.messageText}>
                              {message.content}
                            </div>
                            <div className={styles.messageTime}>
                              {new Date(message.timestamp).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                              {renderMessageStatus(message)}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </>
                  ) : (
                    <div className={styles.emptyChatState}>
                      No messages yet. Start the conversation!
                    </div>
                  )}
                </div>

                <div className={styles.messageInputContainer}>
                  <form 
                    onSubmit={handleSendMessage}
                    className={styles.messageInputWrapper}
                  >
                    {!isMobile && (
                      <div style={{ position: 'relative' }} ref={emojiPickerRef}>
                        <button 
                          type="button" 
                          className={styles.emojiButton}
                          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        >
                          <FontAwesomeIcon icon={faSmile} />
                        </button>
                        {showEmojiPicker && (
                          <div style={{ 
                            position: 'absolute', 
                            bottom: '100%', 
                            left: 0, 
                            zIndex: 1000,
                            transform: 'translateX(-50%)' 
                          }}>
                            <EmojiPicker 
                              onEmojiClick={handleEmojiSelect} 
                              height={400}
                              width={300}
                            />
                          </div>
                        )}
                      </div>
                    )}
                    <textarea
                      ref={messageInputRef}
                      value={messageInput}
                      onChange={(e) => {
                        setMessageInput(e.target.value);
                        adjustInputHeight(e);
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage(e);
                        }
                      }}
                      placeholder="Type a message..."
                      className={styles.messageInput}
                      rows={1}
                    />
                    <button 
                      type="submit" 
                      className={styles.sendButton}
                      disabled={!messageInput.trim()}
                    >
                      <FontAwesomeIcon icon={faPaperPlane} />
                    </button>
                  </form>
                </div>
              </>
            ) : (
              <div className={styles.emptyChatState}>
                Select a contact to start messaging
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(MessagesLayout);